import React, { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, Alert, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  canCreateRecord,
  canUpdateRecord
} from '../../constants/permissions';
import {
  fetchTodayRecord,
  createDeadChickenRecord,
  updateDeadChickenRecord
} from '../../utils/deadChicken';

const { Text } = Typography;

function DeadChickenModal({ open, barnId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [barn, setBarn] = useState(null);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');

  const isEdit = Boolean(record);
  const canCreate = canCreateRecord();
  const canUpdate = canUpdateRecord();
  const canSubmit = isEdit ? canUpdate : canCreate;

  useEffect(() => {
    if (!open || !barnId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setBarn(null);
      setRecord(null);
      form.resetFields();

      try {
        const data = await fetchTodayRecord(barnId);

        if (cancelled) return;

        setBarn(data.barn);
        setRecord(data.record || null);
        form.setFieldsValue({
          employeeCount: data.record?.employee_count ?? null
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, barnId, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      if (isEdit) {
        await updateDeadChickenRecord(barnId, values.employeeCount);
      } else {
        await createDeadChickenRecord(barnId, values.employeeCount);
      }

      onSaved?.();
      onClose();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const permissionHint = !canSubmit
    ? isEdit
      ? t('barn.deadChicken.noUpdatePermission')
      : t('barn.deadChicken.noCreatePermission')
    : '';

  return (
    <Modal
      title={t('barn.deadChicken.title', { barn: barnId })}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('barn.deadChicken.save')}
      cancelText={t('barn.deadChicken.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: loading || !canSubmit }}
      destroyOnClose
      className="dead-chicken-modal"
    >
      {loading ? (
        <div className="py-8 text-center">
          <Spin />
        </div>
      ) : (
        <>
          {error && (
            <Alert type="error" message={error} className="!mb-4" showIcon />
          )}

          {permissionHint && (
            <Alert type="warning" message={permissionHint} className="!mb-4" showIcon />
          )}

          {barn && (
            <div className="mb-4 space-y-1">
              <Text className="block text-gray-600">
                {t('barn.deadChicken.capacity')}: {barn.capacity?.toLocaleString()}
              </Text>
              {barn.area != null && (
                <Text className="block text-gray-600">
                  {t('barn.deadChicken.area')}: {Number(barn.area).toLocaleString()} m²
                </Text>
              )}
            </div>
          )}

          <Form form={form} layout="vertical">
            <Form.Item
              name="employeeCount"
              label={t('barn.deadChicken.countLabel')}
              rules={[
                {
                  required: true,
                  message: t('barn.deadChicken.countRequired')
                },
                {
                  type: 'number',
                  min: 0,
                  message: t('barn.deadChicken.countMin')
                }
              ]}
            >
              <InputNumber
                min={0}
                precision={0}
                className="!w-full"
                placeholder={t('barn.deadChicken.countPlaceholder')}
                disabled={!canSubmit}
              />
            </Form.Item>
          </Form>

          {record && (
            <Text type="secondary" className="text-sm">
              {t('barn.deadChicken.existingRecord')}
            </Text>
          )}
        </>
      )}
    </Modal>
  );
}

export default DeadChickenModal;
